import { createSuccessfulAttempt, createUnsuccessfulAttempt, Try } from '../../../../../types/Try';
import { AssertionException } from '../../../../../utils/log';
import { Guid } from '../../../../model/guid';
import XAPI, { Agent, SendStatementParams, Statement } from '@xapi/xapi';

/** Wraps xApi client for communication with the LRS. */
export interface xApiWrapper {
  /** Stores a state with given identifier to the LRS. */
  storeState<TState>(stateId: string, state: TState): Promise<void>;

  /** Gets state by given identifer from LRS. */
  getStateById<TResult>(stateId: string): Promise<Try<TResult>>;

  /** Sends an activity statement to LRS. */
  sendStatement(createStatement: (actor: Agent) => Statement): Promise<void>;

  /** Gets the actor (learner) from the launch data. */
  getActor(): Agent | null;
}

export const xApiWrapperFactory = (courseGuid: Guid, xApiBaseUrl: string): xApiWrapper => {
  const launchData = XAPI.getTinCanLaunchData();

  if (!(launchData.endpoint && launchData.auth && launchData.actor)) {
    throw AssertionException('TinCan launch data are missing.');
  }

  const xApi = new XAPI({
    endpoint: launchData.endpoint,
    auth: launchData.auth,
  });

  // Do not change the URL unless it is changed on the server as well
  const defaultActivityId = `${xApiBaseUrl}/xapi/activity-id/`;

  const stateSharedData = {
    activityId: launchData.activity_id ?? defaultActivityId + courseGuid,
    agent: launchData.actor as Agent,
    registration: launchData.registration,
  };

  const client: xApiWrapper = {
    getStateById: async <TResult>(stateId: string): Promise<Try<TResult>> => {
      try {
        const response = await xApi.getState({
          ...stateSharedData,
          stateId,
        });

        return response.status !== 200
          ? createUnsuccessfulAttempt()
          : createSuccessfulAttempt(response.data as TResult);
      } catch {
        return createUnsuccessfulAttempt();
      }
    },

    storeState: async <TState>(stateId: string, state: TState): Promise<void> => {
      await xApi.setState({
        ...stateSharedData,
        stateId,
        state,
      });
    },

    sendStatement: async (createStatement: (actor: Agent) => Statement): Promise<void> => {
      const statement = createStatement(launchData.actor as Agent);
      if (stateSharedData.registration) {
        statement.context = { ...statement.context, registration: stateSharedData.registration };
      }
      const params: SendStatementParams = { statement };
      await xApi.sendStatement(params);
    },

    getActor: (): Agent | null => {
      return (launchData.actor as Agent) ?? null;
    },
  };

  return client;
};
